import { Component, inject, Input, OnChanges, OnInit, Signal, SimpleChanges } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AsyncPipe } from '@angular/common';
import { LoaderComponent } from '@shared/component/loader/loader.component';
import { PrettyDatePipe } from '@shared/pipe/pretty-date.pipe';
import { TranslocoPipe } from '@ngneat/transloco';
import { UserService } from '@shared/service/user.service';
import { DialogService } from '@shared/service/dialog.service';
import { Store } from '@ngxs/store';
import { HistoryFilter, Paging, RoomTranscript, RoomUser } from '@shared/transport.interface';
import { UserState } from '@shared/store/user/user-state.service';
import { finalize } from 'rxjs';
import { UtcDatePipe } from '@shared/pipe/utc-date.pipe';
import { DateLineComponent } from '@shared/component/ui/date-line/date-line.component';
import { InitialsAvatarComponent } from '@shared/component/ui/initials-avatar/initials-avatar.component';
import { addIcons } from 'ionicons';
import { ellipsisVertical, trashOutline } from 'ionicons/icons';
import { InfiniteScrollCustomEvent, IonicModule } from '@ionic/angular';

@UntilDestroy()
@Component({
  selector: 'app-transcript-history',
  standalone: true,
  imports: [
    AsyncPipe,
    DateLineComponent,
    InitialsAvatarComponent,
    LoaderComponent,
    PrettyDatePipe,
    TranslocoPipe,
    UtcDatePipe,
    IonicModule,
  ],
  templateUrl: './transcript-history.component.html',
  styleUrl: './transcript-history.component.scss'
})
export class TranscriptHistoryComponent implements OnInit, OnChanges {
  @Input() filterChanged = false;
  @Input() filter: HistoryFilter;

  private _userService = inject(UserService);
  private _dialogService = inject(DialogService);
  private _store = inject(Store);

  protected me: Signal<RoomUser> = this._store.selectSignal(UserState.getUser);
  transcripts: RoomTranscript[] = [];
  loading = false;
  upgradeTariff = false;
  private _paging: Paging = {
    currentPage: 1,
    totalPages: 0,
    pageSize: 10,
  };
  protected loadingMore = false;
  protected empty = false;

  constructor() {
    addIcons({ ellipsisVertical, trashOutline });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.filterChanged?.currentValue === true) {
      this.ngOnInit();
    }
  }

  ngOnInit(): void {
    this.loading = true;
    this._userService.historyTranscripts(this._paging.pageSize, (this._paging.currentPage - 1) * this._paging.pageSize, this.filter)
      .pipe(untilDestroyed(this), finalize(() => this.loading = false))
      .subscribe({
        next: res => {
          this._paging.totalPages = res.total;
          this.transcripts = res.list;
        },
        error: () => {
          this.upgradeTariff = true;
        }
      });
  }

  loadMoreData(ev: InfiniteScrollCustomEvent): void {
    if (this.loadingMore || this.empty) {
      return;
    }
    this.loadingMore = true;
    this._paging.currentPage++;
    this._userService.historyTranscripts(this._paging.pageSize, (this._paging.currentPage - 1) * this._paging.pageSize)
      .pipe(untilDestroyed(this), finalize(() => this.loadingMore = false))
      .subscribe(res => {
        this.transcripts = this.transcripts.concat(res.list);
        if (!res.list.length || res.list.length < this._paging.pageSize) {
          this.empty = true;
        }
        ev.target.complete().then();
      });
  }

  deleteTranscript(transcript: RoomTranscript): void {
    this._dialogService.deleteTranscript(transcript)
      .subscribe(res => {
        this.deleteTranscriptFromArray(res);
      });
  }

  shareTranscript(transcript: RoomTranscript): void {

  }

  downloadPdf(transcript: RoomTranscript): void {
    alert('Not implemented');
  }

  showTranscript(transcript: RoomTranscript): void {
    this._dialogService.showTranscript(transcript)
      .then(res => {
        if (res?.deleted) {
          this.deleteTranscriptFromArray(res);
        }
      });
  }

  private deleteTranscriptFromArray(res: RoomTranscript) {
    if (res) {
      const ind = this.transcripts.findIndex(s => s.id === res.id);
      if (ind >= 0) {
        this.transcripts.splice(ind, 1);
      }
    }
  }
}
